import Masonry from 'masonry-layout'

/**
 * Evaluate a CSS length expression to its length in pixel
 *
 * @param {Element} element
 * @param {string} expression
 * @param {string} property
 * @return {number}
 */
function evaluateCssLength(element, expression, property = 'width') {
  let calculateWidth = () => parseFloat(getComputedStyle(element).getPropertyValue(property))
  let preValue = element.style.getPropertyValue(property)
  let prePriority = element.style.getPropertyPriority(property)

  let preComputedValue = calculateWidth()

  element.style.setProperty(property, expression, 'important')

  if (element.style.getPropertyValue(property).length === 0) return NaN

  let result = calculateWidth()

  // Add an additional check if expression did not change result
  if (result === preComputedValue) {
    element.style.setProperty(property, `calc(${expression} + 1px)`, 'important')

    if (calculateWidth() === result) return NaN
  }

  element.style.setProperty(property, preValue, prePriority)

  return result
}

export default Alpine => {
  let styleBlock

  function getLayoutOptions(el) {
    let style = getComputedStyle(el)
    let columns = parseFloat(style.getPropertyValue('--masonry-columns')) || 1

    let measureHelper = document.createElement('div')
    el.append(measureHelper)
    let gutter = evaluateCssLength(measureHelper, 'var(--masonry-gutter)')
    measureHelper.remove()

    let contentWidth =
      el.getBoundingClientRect().width -
      parseFloat(style.paddingLeft) -
      parseFloat(style.paddingRight)
    let columnWidth = (contentWidth - (columns - 1) * gutter) / columns

    return {
      columnWidth,
      gutter,
    }
  }

  /**
   * @param {Masonry} masonry
   */
  function applyLayout(masonry) {
    let el = masonry.element
    el.style.setProperty('--masonry-column-width', `${masonry.options.columnWidth}px`)
    masonry.layout()
  }

  /**
   * Update environment (screen size, ...) or options (Alpine data)
   *
   * @param {Masonry} masonry
   */
  function update(masonry) {
    Object.assign(masonry.options, getLayoutOptions(masonry.element))
    applyLayout(masonry)
    masonry.layout()
  }

  let masonryCounter = 0
  Alpine.directive('masonry', (el, { expression }, { evaluateLater, effect, cleanup }) => {
    if (window.matchMedia('print').matches) return

    masonryCounter++

    if (!styleBlock) {
      styleBlock = document.createElement('style')
      styleBlock.innerHTML = `
      .masonry-item {
        --masonry-item-size: min(var(--masonry-size, 1), var(--masonry-columns, 1));
        --masonry-item-width: calc(
          var(--masonry-item-size) * var(--masonry-column-width) +
          (var(--masonry-item-size) - 1) * var(--masonry-gutter)
        );
        width: var(--masonry-item-width) !important;
        max-width: var(--masonry-item-width) !important;
        margin: 0 !important;
        margin-bottom: var(--masonry-gutter, 0) !important;
      }

      @media print {
        .masonry-item {
          position: static !important;
          margin: 0 !important;
          padding: 0 !important;
        }
      }

      .resizing {
        overflow: hidden;
      }

      .resizing > .masonry-item {
        transition: opacity 125ms;
        opacity: 0;
      }
      `
      document.head.appendChild(styleBlock)
    }

    /**
     * @type {Masonry}
     */
    let masonry

    /**
     * @type {() => void}
     */
    let resizeHandler

    /**
     * @type {() => void}
     */
    let resizeEndHandler

    /**
     * @type {() => void}
     */
    let deferredResizeEndHandler

    let getAdditionalOptions = evaluateLater(expression || '{}')

    effect(() => {
      getAdditionalOptions(additionalOptions => {
        if (!masonry) {
          // Initialize Masonry
          let children = [...el.children]
          if ('itemSelector' in additionalOptions) {
            children = children.filter(child => child.matches(additionalOptions.itemSelector))
          }
          for (let child of children) {
            child.classList.add('masonry-item')
          }

          let layoutOptions = getLayoutOptions(el)

          masonry = new Masonry(el, {
            itemSelector: '.masonry-item',
            resize: false,
            initLayout: false,
            transitionDuration: 0,
            ...additionalOptions,
            ...layoutOptions,
          })

          masonry.on('layoutComplete', items => {
            if (items.length === 0) return

            const layout = items[0].layout

            const containerWidth = layout.containerWidth
            const gutter = layout.gutter
            const columns = layout.cols
            el.dataset.columns = columns

            for (const item of items) {
              const positionX = item.position.x
              item.element.dataset.column = Math.floor(positionX / (containerWidth / columns)) + 1
            }
          })

          applyLayout(masonry)

          let resizing = false
          let width = window.innerWidth
          resizeHandler = () => {
            if (!resizing && width !== window.innerWidth) {
              el.classList.add('resizing')
              resizing = true
            }
          }
          resizeEndHandler = () => {
            resizing = false
            width = window.innerWidth
            el.classList.remove('resizing')
            update(masonry)
          }
          deferredResizeEndHandler = () => {
            setTimeout(resizeEndHandler, 0)
          }

          window.addEventListener('resize', resizeHandler)
          window.addEventListener('resizeEnd', resizeEndHandler)
          document.addEventListener('interceptor:after', resizeEndHandler)
        } else {
          // Changed options
          update(masonry)
        }
      })
    })

    cleanup(() => {
      masonryCounter--

      el.style.removeProperty('--masonry-column-width')

      for (let item of masonry.items) {
        item.element.classList.remove('masonry-item')
      }

      window.removeEventListener('resize', resizeHandler)
      window.removeEventListener('resizeEnd', resizeEndHandler)
      document.removeEventListener('interceptor:after', resizeEndHandler)
      masonry.destroy()

      if (masonryCounter === 0) {
        styleBlock.remove()
        styleBlock = undefined
      }
    })
  })
}
